<template>
    <quote-info :quote-id="selectedQuoteId ?? 0" v-model="showMoreInfo" :isPayMonthly="isPayMonthly ?? false" />
    <asli-form-section :title="pageTitle">
        <div>
            <Paragraph>{{ quotesTextToShow() }}</Paragraph>
            <Paragraph v-if="quoteObjects.length > 0">Your quote is valid until {{ dateValidTo }}.</Paragraph>
        </div>
        <div class="mt-[30px]">
            <asli-quote-display-podium
                :quotes="quoteObjects"
                @click-buy-now="selectQuote($event)"
                @click-more-info="moreInfoClicked($event)"
                :is-pay-monthly="isPayMonthly"
                :show-cover-level-box="showCoverLevelBox"
            />
        </div>
        <div v-if="quoteObjects.length > 0" class="flex justify-center mt-[30px]">
            <asli-toggle
                button-aria-label="Pay Annually"
                v-model="isPayMonthly"
                :values="{
                    right: { text: 'Pay Annually', value: false },
                    left: { text: 'Pay Monthly', value: true }
                }"
                pressed-key="right"
                @click="toggleIsPayMonthly"
            />
        </div>
    </asli-form-section>
    <asli-form-section>
        <div v-if="quoteObjects.length > 0" class="mt-[30px]">
            <quote-table
                :quotes="quotes"
                @select-quote="selectQuote($event)"
                @click-more-info="moreInfoClicked($event)"
            />
        </div>
    </asli-form-section>

    <button-bar sticky>
        <asli-button id="quotes-back" color="secondary" type="button" @click="goBack"> Back </asli-button>
    </button-bar>
</template>

<script lang="ts" setup>
import QuoteInfo from "@/components/QuoteInfo.vue";
import { useProposalStore } from "@/stores/risk/risk";
import {
    AsliFormSection,
    QuoteDisplayProp,
    AsliQuoteDisplayPodium,
    AsliToggle,
    AsliButton,
    QuoteDisplayIcon
} from "@mbs-npm/anpost-ui";
import { PropType, computed, ref } from "vue";
import Paragraph from "@/components/Paragraph.vue";
import QuoteTable from "./MultiQuote/QuoteTable.vue";
import { getFormattedLogoName, scrollToTop, formatDate } from "@/utils";
import { useRouter } from "vue-router";
import { FORM_MODE, ROUTE_NAMES } from "@/router/types";
import { useMainStore } from "@/stores/main";
import { FormRouteName } from "@/router/utils";
import ButtonBar from "@/components/ButtonBar.vue";
import { Quote } from "@/stores/risk/types/types";
import { BRAND_PHONE_NUMBER } from "@/static/constants";

const mainStore = useMainStore();
const proposalStore = useProposalStore();
const router = useRouter();

const firstName = computed(() => proposalStore.proposal.risk.proposer.personal_details.first_name);
const props = defineProps({
    quotes: {
        type: Array as PropType<Array<Quote>>,
        required: true
    }
});

const quoteRef = computed(() => proposalStore?.proposal?.quote?.reference);
const isPayMonthly = computed(() => proposalStore?.proposal?.is_pay_monthly);
const pageTitle = computed(() =>
    proposalStore.proposal?.is_quoted ? `Quote Results - ${quoteRef.value}` : "Quote Results"
);
const dateValidTo = computed(() => formatDate(proposalStore.proposal.quote?.date_valid_to));

const showMoreInfo = ref(false);
const selectedQuoteId = ref<number | null>(null);

const showCoverLevelBox = false;

const homeIcons = ref<Array<QuoteDisplayIcon>>([
    { icon: "buildings", text: "Buildings" },
    { icon: "contents", text: "Contents" }
]);

const filterIcons = quote => {
    const buildingsCoverAmount = proposalStore?.proposal?.risk?.cover_details?.buildings_cover_amount;
    const contentsCoverAmount = proposalStore?.proposal?.risk?.cover_details?.contents_cover_amount;

    let filteredIcons: Array<Object> = [];
    if (buildingsCoverAmount && buildingsCoverAmount > 0) {
        filteredIcons.push(homeIcons.value[0]);
    }
    if (contentsCoverAmount && contentsCoverAmount > 0) {
        filteredIcons.push(homeIcons.value[1]);
    }

    return filteredIcons;
};

const quoteObjects = computed<Array<QuoteDisplayProp>>(() => {
    return props.quotes.map<QuoteDisplayProp>(quote => ({
        id: quote.id,
        insurerName: quote.insurer_name,
        premium: quote.premium,
        icons: filterIcons(quote),
        logo: getLogoPath(quote.insurer_name ?? "").toString(),
        monthly_deposit: quote.monthly_deposit,
        monthly_payment: quote.monthly_payment,
        monthly_total_payable: quote.monthly_total_payable,
        monthly_installments: quote.monthly_installments,
        monthly_apr: quote.monthly_apr,
        monthly_interest: quote.monthly_interest
    }));
});

const toggleIsPayMonthly = () => {
    proposalStore.toggleIsPayMonthly();
    if (isPayMonthly.value === true) {
        mainStore.setIsMonthly(true);
    } else {
        mainStore.setIsMonthly(false);
    }
};

function getLogoPath(insurer: string) {
    return new URL(`../../../assets/logos/${getFormattedLogoName(insurer)}.png`, import.meta.url).href;
}

const quotesTextToShow = () => {
    if (quoteObjects.value.length > 3) {
        return `Great news ${firstName.value}. We have found ${quoteObjects.value.length} quotes for you! Here are
        the 3 quotes with the best price.`;
    } else if (quoteObjects.value.length <= 3 && quoteObjects.value.length > 0) {
        return `Great news ${firstName.value}. We have found ${quoteObjects.value.length}
        ${quoteObjects.value.length > 1 ? "quotes" : "quote"} with the best price.`;
    } else
        return `Sorry, we could not find any quotes for you today. Please check your details and retry, or contact
            us on ${BRAND_PHONE_NUMBER} to discuss.`;
};

async function selectQuote(id: number) {
    try {
        mainStore.setLoadingState(true);
        await proposalStore.selectQuote(id);
        await proposalStore.UpdatePaymentChoice(isPayMonthly.value);
        await router.push({
            name: ROUTE_NAMES.Marketing
        });
    } finally {
        mainStore.setLoadingState(false);
    }
}

function moreInfoClicked(id: number) {
    selectedQuoteId.value = id;
    showMoreInfo.value = true;
}

function goBack() {
    router.push({
        name: FormRouteName(ROUTE_NAMES.YourCover, FORM_MODE.EDIT),
        query: {
            uuid: proposalStore.proposal.uuid
        }
    });
}

function endRecalculate() {
    scrollToTop();
}
</script>
